import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import AccordionUnstyled, {
  AccordionDefaultProps,
  AccordionPropTypes,
} from '@ecp/common/src/components/unstyled/accordion/AccordionUnstyled';
import AccordionBodyUnstyled, {
  AccordionBodyPropTypes,
} from '@ecp/common/src/components/unstyled/accordion/AccordionBodyUnstyled';
import AccordionControlUnstyled, {
  AccordionControlDefaultProps,
  AccordionControlPropTypes,
} from '@ecp/common/src/components/unstyled/accordion/AccordionControlUnstyled';
import { useAccordionContext } from '@ecp/common/src/components/unstyled/accordion/useAccordionContext';
import AccordionHeaderUnstyled, {
  AccordionHeaderPropTypes,
} from '@ecp/common/src/components/unstyled/accordion/AccordionHeaderUnstyled';
import { ReactComponent as IconOpen } from '@ecp/common/src/assets/icon/icon__arrow--below.svg';

const StyledAccordion = styled(FlexBox)`
  width: 100%;
  align-items: flex-start;
`;

const Accordion = ({ children, ...props }) => {
  return (
    <AccordionUnstyled rootComponent={StyledAccordion} {...props}>
      {children}
    </AccordionUnstyled>
  );
};

Accordion.propTypes = AccordionPropTypes;
Accordion.defaultProps = AccordionDefaultProps;

const StyledAccordionHeader = styled(FlexBox).attrs(({ open, style }) => ({
  style: {
    ...style,
    borderRadius: open ? '6px 6px 0 0' : '6px',
  },
}))`
  width: 100%;
  border: 1px solid ${boTheme.color.sub13};
  background-color: ${boTheme.color.sub14};
  padding: 16px 20px;
`;

const AccordionHeader = ({ children, ...props }) => {
  const { open } = useAccordionContext();

  return (
    <AccordionHeaderUnstyled rootComponent={StyledAccordionHeader} open={open} {...props}>
      {children}
    </AccordionHeaderUnstyled>
  );
};

AccordionHeader.propTypes = AccordionHeaderPropTypes;

const AccordionHeaderTitle = styled(TextBox)`
  font-size: ${boTheme.font.size.basic};
  font-weight: ${boTheme.font.weight.bold};
  line-height: 100%;
`;

const AccordionHeaderDescription = styled(TextBox)`
  font-size: ${boTheme.font.size.basic};
  font-weight: ${boTheme.font.weight.regular};
  line-height: 100%;
`;

const AccordionHeaderContent = styled(TextBox)`
  flex-grow: 1;
  justify-content: flex-end;
`;

const StyledAccordionBody = styled(FlexBox)`
  width: 100%;
  padding: 20px;
  border: 1px solid ${boTheme.color.sub13};
  border-radius: 0 0 6px 6px;
  margin-top: -1px;
`;

const AccordionBody = (props) => {
  const { children } = props;

  return (
    <AccordionBodyUnstyled undead rootComponent={StyledAccordionBody}>
      {children}
    </AccordionBodyUnstyled>
  );
};

AccordionBody.propTypes = AccordionBodyPropTypes;

const StyledAccordionControl = styled(FlexBox)`
  flex-grow: 1;
  justify-content: flex-end;
`;

const AccordionControl = ({ children, button: { width, height, padding } = {}, ...props }) => {
  return (
    <AccordionControlUnstyled
      rootComponent={StyledAccordionControl}
      buttonElement={
        <>
          <Spacing value={padding} />
          <IconOpen color={boTheme.color.sub2} width={width} height={height} />
          <Spacing bottom={'2px'} />
        </>
      }
      {...props}
    >
      {children}
    </AccordionControlUnstyled>
  );
};

AccordionControl.propTypes = {
  ...AccordionControlPropTypes,
  button: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
    padding: PropTypes.string,
  }),
};

AccordionControl.defaultProps = {
  ...AccordionControlDefaultProps,
  button: {
    width: '10px',
    height: '6px',
    padding: '0',
  },
};

Accordion.Header = AccordionHeader;
Accordion.HeaderTitle = AccordionHeaderTitle;
Accordion.HeaderDescription = AccordionHeaderDescription;
Accordion.HeaderContent = AccordionHeaderContent;
Accordion.Control = AccordionControl;
Accordion.Body = AccordionBody;

export default Accordion;
